import React, { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Coins, History, LogOut, Sparkles, Swords, Skull, Clock, ChevronRight,
  User, CheckCircle2, XCircle, AlertCircle, KeyRound, Loader2, AlertTriangle, Download,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { donateApi, authApi } from "../api/auth";
import { downloadAccountBackup } from "../utils/accountBackup";
import { sanitizePassword } from "../utils/inputSanitize";
import { Input } from "../components/ui/input";
import { useContentObject } from "../hooks/useContentData";

const AccountPanel = () => {
  const { user, loading, logout, refresh } = useAuth();
  const { data: t } = useContentObject("account.panel");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [banner, setBanner] = useState(null);

  // Handle return from PayPal
  useEffect(() => {
    const result = params.get("donation");
    if (result) {
      const orderUid = params.get("order") || "";
      const fill = (s) => (s || "").replace(/\{order\}/g, orderUid);
      const map = {
        ok:      { type: "ok",      title: t.banner_ok_title,      msg: fill(t.banner_ok_msg) },
        fail:    { type: "fail",    title: t.banner_fail_title,    msg: fill(t.banner_fail_msg) },
        pending: { type: "pending", title: t.banner_pending_title, msg: fill(t.banner_pending_msg) },
      };
      setBanner(map[result] || null);
      params.delete("donation");
      params.delete("order");
      setParams(params, { replace: true });
      // Refrescar info por si se actualizó el total de coins
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Load donation history
  useEffect(() => {
    if (!user) return;
    donateApi.history().then(d => setHistory(d.orders || [])).catch(() => {});
  }, [user]);

  if (loading) return <main className="bg-[#0a0a0d] min-h-screen flex items-center justify-center text-[#8a8a92]">Cargando...</main>;
  if (!user) return <Navigate to="/account" replace />;

  return (
    <main className="bg-[#0a0a0d] min-h-screen pt-32 pb-20">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Banner */}
        {banner && <DonationBanner banner={banner} onClose={() => setBanner(null)} />}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="ornament"><span>{t.eyebrow}</span></div>
            <h1 className="section-title mt-3 text-3xl md:text-4xl">{user.account}</h1>
          </div>
          <button onClick={async () => { await logout(); navigate("/"); }}
            className="btn-ghost px-5 py-2 text-xs rounded-sm flex items-center gap-2 self-start">
            <LogOut className="w-3.5 h-3.5" /> Cerrar Sesión
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={<User />} label={t.stat_characters} value={user.characters?.length || 0} />
          <StatCard icon={<Coins />} label={t.stat_coins} value={user.donations?.total_coins?.toLocaleString() || 0} accent />
          <StatCard icon={<History />} label={t.stat_orders} value={user.donations?.total_orders || 0} />
          <StatCard icon={<Sparkles />} label={t.stat_access} value={user.access_level === 0 ? "Player" : "GM"} />
        </div>

        {/* Donate CTA */}
        <Link to="/account/donate"
          className="group block glass rounded-sm p-7 mb-10 hover:translate-y-[-3px] transition-all relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(212,168,67,0.05)] to-transparent" />
          <div className="relative flex items-center gap-5">
            <div className="w-14 h-14 rounded-sm bg-gradient-to-br from-[#f5c542] via-[#d4a843] to-[#8b6914] flex items-center justify-center text-[#1a1408]">
              <Coins className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h3 className="font-display font-bold text-xl text-[#f0ebe0] group-hover:text-[#f5c542] transition-colors">
                {t.donate_cta_title}
              </h3>
              <p className="text-sm text-[#b8b3a8] mt-1">
                {t.donate_cta_text}
              </p>
            </div>
            <ChevronRight className="w-6 h-6 text-[#d4a843] group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>

        {/* Change password */}
        <ChangePasswordCard accountName={user.account} />

        {/* Characters */}
        <div className="glass rounded-sm overflow-hidden mb-10">
          <div className="px-6 py-4 border-b border-[rgba(212,168,67,0.15)] bg-[rgba(212,168,67,0.04)] flex items-center justify-between">
            <h3 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] flex items-center gap-2">
              <Swords className="w-3.5 h-3.5" /> {t.characters_title}
            </h3>
            <span className="font-mono text-xs text-[#8a8a92]">{user.characters?.length || 0} chars</span>
          </div>
          {user.characters?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#14141a]">
                  <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#8a8a92]">
                    {["Nombre", "Clase", "Raza", "Lvl", "PvP", "PK", "Online", "Status"].map(h => (
                      <th key={h} className="px-5 py-3 font-display">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {user.characters.map((c, i) => (
                    <tr key={i} className="border-t border-[rgba(212,168,67,0.08)] hover:bg-[rgba(212,168,67,0.04)] transition-colors">
                      <td className="px-5 py-3.5 font-display font-semibold text-[#f0ebe0]">{c.name}</td>
                      <td className="px-5 py-3.5 text-sm text-[#b8b3a8]">{c.class}</td>
                      <td className="px-5 py-3.5 text-sm text-[#b8b3a8]">{c.race}</td>
                      <td className="px-5 py-3.5 font-mono text-sm text-[#d4a843]">{c.level}</td>
                      <td className="px-5 py-3.5 font-mono text-sm text-[#10b981]">{c.pvp}</td>
                      <td className="px-5 py-3.5 font-mono text-sm text-[#b03030]"><span className="inline-flex items-center gap-1"><Skull className="w-3 h-3" />{c.pk}</span></td>
                      <td className="px-5 py-3.5 font-mono text-sm text-[#8a8a92]"><span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{c.online_h}h</span></td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono ${c.online ? "text-emerald-400" : "text-[#6f6c66]"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${c.online ? "bg-emerald-400 animate-pulse" : "bg-[#6f6c66]"}`} />
                          {c.online ? "online" : "offline"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-[#8a8a92] text-sm">{t.characters_empty}</div>
          )}
        </div>

        {/* Donation History */}
        <div className="glass rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-[rgba(212,168,67,0.15)] bg-[rgba(212,168,67,0.04)] flex items-center justify-between">
            <h3 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] flex items-center gap-2">
              <History className="w-3.5 h-3.5" /> {t.history_title}
            </h3>
            <span className="font-mono text-xs text-[#8a8a92]">{history.length} ordenes</span>
          </div>
          {history.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#14141a]">
                  <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#8a8a92]">
                    {["Fecha", "Order", "Método", "Monto", "Coins", "Status"].map(h => (
                      <th key={h} className="px-5 py-3 font-display">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((o) => (
                    <tr key={o.order_uid} className="border-t border-[rgba(212,168,67,0.08)] hover:bg-[rgba(212,168,67,0.04)] transition-colors">
                      <td className="px-5 py-3.5 text-xs text-[#b8b3a8] font-mono">{o.created_at}</td>
                      <td className="px-5 py-3.5 text-xs text-[#d4a843] font-mono">{o.order_uid}</td>
                      <td className="px-5 py-3.5 text-sm text-[#f0ebe0] uppercase">{o.method}</td>
                      <td className="px-5 py-3.5 font-mono text-sm text-[#f0ebe0]">{o.amount} {o.currency}</td>
                      <td className="px-5 py-3.5 font-mono text-sm text-[#10b981]">+{o.coins.toLocaleString()}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={o.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-[#8a8a92] text-sm">{t.history_empty}</div>
          )}
        </div>
      </div>
    </main>
  );
};

const DonationBanner = ({ banner, onClose }) => {
  const cfg = {
    ok:      { Icon: CheckCircle2, color: "#10b981", bg: "bg-[#0d2620]", border: "border-emerald-500/40" },
    fail:    { Icon: XCircle,      color: "#ef4444", bg: "bg-[#260d0d]", border: "border-red-500/40" },
    pending: { Icon: AlertCircle,  color: "#d4a843", bg: "bg-[#2a2410]", border: "border-yellow-500/40" },
  }[banner.type];
  const { Icon, color, bg, border } = cfg;
  return (
    <div className={`${bg} ${border} border rounded-sm p-5 mb-8 flex items-start gap-4 animate-fade-up`}>
      <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color }} />
      <div className="flex-1">
        <h4 className="font-display font-bold mb-1" style={{ color }}>{banner.title}</h4>
        <p className="text-sm text-[#b8b3a8]">{banner.msg}</p>
      </div>
      <button onClick={onClose} className="text-[#8a8a92] hover:text-[#f0ebe0] text-xl leading-none">×</button>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const cfg = {
    approved:  { color: "#10b981", label: "APROBADA" },
    pending:   { color: "#d4a843", label: "PENDIENTE" },
    rejected:  { color: "#ef4444", label: "RECHAZADA" },
    refunded:  { color: "#a855f7", label: "REEMBOLSADA" },
    cancelled: { color: "#6f6c66", label: "CANCELADA" },
    expired:   { color: "#6f6c66", label: "EXPIRADA" },
  }[status] || { color: "#8a8a92", label: status.toUpperCase() };
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-sm border"
      style={{ color: cfg.color, borderColor: cfg.color + "55", backgroundColor: cfg.color + "11" }}>
      {cfg.label}
    </span>
  );
};

const StatCard = ({ icon, label, value, accent }) => (
  <div className={`glass rounded-sm p-5 ${accent ? "border-[rgba(212,168,67,0.4)]" : ""}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent ? "bg-[rgba(212,168,67,0.15)] text-[#f5c542]" : "bg-[#21212b] text-[#b8b3a8]"}`}>
        {React.cloneElement(icon, { className: "w-4 h-4" })}
      </div>
      <span className="text-[10px] tracking-[0.22em] uppercase text-[#8a8a92]">{label}</span>
    </div>
    <div className="font-display font-extrabold text-2xl text-[#f0ebe0]">{value}</div>
  </div>
);

function ChangePasswordCard({ accountName }) {
  const { data: backupTexts } = useContentObject("account.backup");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setForm({ current_password: "", new_password: "", confirm: "" });
    setError(""); setSuccess(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (form.new_password !== form.confirm) {
      setError("Las contraseñas nuevas no coinciden"); return;
    }
    if (form.new_password.length < 4 || form.new_password.length > 16) {
      setError("La nueva contraseña debe tener entre 4 y 16 caracteres"); return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
      });
      downloadAccountBackup({
        action: "change_password",
        account: res.account,
        password: form.new_password,
        meta: res.meta,
        texts: backupTexts,
      });
      setSuccess(true);
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setError(err.message || "Error al cambiar la contraseña");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="glass rounded-sm overflow-hidden mb-10" data-testid="change-password-card">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); if (open) reset(); }}
        data-testid="change-password-toggle"
        className="w-full px-6 py-4 border-b border-[rgba(212,168,67,0.15)] bg-[rgba(212,168,67,0.04)] flex items-center justify-between hover:bg-[rgba(212,168,67,0.08)] transition"
      >
        <span className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] flex items-center gap-2">
          <KeyRound className="w-3.5 h-3.5" /> Cambiar Contraseña
        </span>
        <ChevronRight className={`w-4 h-4 text-[#d4a843] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="p-6">
          {success ? (
            <div className="text-center space-y-3" data-testid="change-password-success">
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto" />
              <p className="text-sm text-white">Contraseña actualizada correctamente.</p>
              <p className="text-xs text-[#b8b3a8] flex items-center justify-center gap-1.5">
                <Download className="w-3 h-3" /> Se descargó un TXT con la nueva contraseña.
              </p>
              <button onClick={reset} className="text-xs text-[#d4a843] hover:underline">Cambiar de nuevo</button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4 max-w-md">
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4a843] font-display block mb-2">Contraseña actual</label>
                <Input type="password" value={form.current_password}
                  onChange={(e) => setForm((f) => ({ ...f, current_password: sanitizePassword(e.target.value) }))}
                  className="bg-[#111118] border-[rgba(212,168,67,0.2)] text-[#f0ebe0] font-mono h-11"
                  required maxLength={16} data-testid="change-current-password" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4a843] font-display block mb-2">Nueva contraseña</label>
                <Input type="password" value={form.new_password}
                  onChange={(e) => setForm((f) => ({ ...f, new_password: sanitizePassword(e.target.value) }))}
                  className="bg-[#111118] border-[rgba(212,168,67,0.2)] text-[#f0ebe0] font-mono h-11"
                  required minLength={4} maxLength={16} placeholder="4-16 letras y numeros" data-testid="change-new-password" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#d4a843] font-display block mb-2">Confirmar nueva contraseña</label>
                <Input type="password" value={form.confirm}
                  onChange={(e) => setForm((f) => ({ ...f, confirm: sanitizePassword(e.target.value) }))}
                  className="bg-[#111118] border-[rgba(212,168,67,0.2)] text-[#f0ebe0] font-mono h-11"
                  required minLength={4} maxLength={16} data-testid="change-confirm-password" />
              </div>
              {error && (
                <div className="flex items-start gap-2 p-3 bg-[#260d0d]/60 border border-[#b03030]/40 rounded-sm text-sm text-[#ef4444]">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
                </div>
              )}
              <button type="submit" disabled={submitting}
                className="btn-gold py-3 px-6 text-xs rounded-sm flex items-center gap-2 disabled:opacity-60"
                data-testid="change-password-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Cambiando...</> : <><KeyRound className="w-4 h-4" /> Actualizar Contraseña</>}
              </button>
              <p className="text-[10px] text-[#7e7768]">
                Al cambiar la contraseña se cerrarán todas tus otras sesiones (la actual sigue activa).
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountPanel;
